import { MutableRefObject, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material';
import {
  CompanyOverviewIllustration,
  HomePageIllustration,
  WorkflowCardsIllustration,
  useCurrentUserInfo,
  useOnboardingStore,
  environmentSettingsQuery,
  isValidColour,
  dianaAIQuery,
  localStorageUtil,
  LocalStorageKey,
  useAppStore,
  companyQuery,
  IEnvironmentSettings,
  getUserName,
  sessionInfoQuery,
  queryClient,
  useSessionInfoOnSuccess,
  axios,
  isEmptyFile,
} from '@diana/core';
import { BusinessForm } from './Steps/BusinessForm';
import { ONBOARDING_PAGE_NS } from './i18n';
import { BusinessKeyAreas } from './Steps/BusinessKeyAreas';
import { CustomiseEcosystem } from './Steps/CustomiseEcosystem';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { routeUrls } from '../../app/Routing/RouteUrls';
import { useNavigate } from 'react-router-dom';
import { useCompanyResearch } from './useCompanyResearch';

export interface IOnboardingStep {
  component: ReactNode;
  banner: {
    title: string;
    description: string;
    illustration: ReactNode;
  };
  onSubmit: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  disableSubmit?: boolean;
}

export const useOnboardingPageSteps = () => {
  const { t } = useTranslation(ONBOARDING_PAGE_NS);
  const { palette } = useTheme();
  const abortEcosystemCreate = useRef<AbortController>(new AbortController());
  const abortWorkflowGenerate = useRef<AbortController>(new AbortController());
  const navigate = useNavigate();
  const onSessionInfoSuccess = useSessionInfoOnSuccess();
  const info = useCurrentUserInfo();

  const companyName = useOnboardingStore((store) => store.companyName) ?? info?.companyName ?? '';
  const ecosystemName = useOnboardingStore((store) => store.ecosystemName) ?? '';
  const industry = useOnboardingStore((store) => store.industry) ?? '';
  const keyAreas = useOnboardingStore((store) => store.keyAreas);
  const research = useOnboardingStore((store) => store.research);
  const color = useOnboardingStore((store) => store.color) ?? palette.primary.main;
  const websiteUrl = useOnboardingStore((store) => store.websiteUrl) ?? '';
  const logoFile = useOnboardingStore((store) => store.logoFile);
  const setGeneratedEcosystemName = useOnboardingStore((store) => store.setGeneratedEcosystemName);
  const generatedEcosystemName = useOnboardingStore((store) => store.generatedEcosystemName);
  const isError = useOnboardingStore((store) => store.isError);
  const isLoadingRequest = useOnboardingStore((store) => store.isLoadingRequest);
  const setIsLoadingRequest = useOnboardingStore((store) => store.setIsLoadingRequest);
  const clearStore = useOnboardingStore((store) => store.clear);
  const setGlobalLoading = useAppStore((store) => store.setLoading);
  const setIsGeneratingWorkflows = useAppStore((store) => store.setIsGeneratingWorkflows);

  const isValidColor = useMemo(() => {
    return isValidColour(color);
  }, [color]);

  const handleAbort = useCallback((ref: MutableRefObject<AbortController>) => {
    ref.current.abort();
    ref.current = new AbortController();
  }, []);

  const updateEcosystem = useCallback(
    async (body: Partial<IEnvironmentSettings>) => {
      return await environmentSettingsQuery
        .updateEnvironment()
        .fn({ environmentName: ecosystemName, body });
    },
    [ecosystemName],
  );

  // 1st step functionality
  const { mutate: researchCompany, isPending: isResearchingCompany } = useCompanyResearch();

  const createQuery = environmentSettingsQuery.create();
  const { mutate: createEcosystem, isPending: isCreatingEcosystem } = useMutation({
    mutationKey: createQuery.key,
    mutationFn: () =>
      createQuery.fn({
        ecosystem: {
          name: ecosystemName,
          description: ecosystemName,
          colors: {
            primary: palette.primary.main,
            text: palette.getContrastText(palette.primary.main),
          },
        },
        abort: abortEcosystemCreate.current,
      }),
    onSuccess: (settings) => {
      setGeneratedEcosystemName(settings.name);
    },
  });
  const handleCreateEcosystem = useCallback(() => {
    if (!generatedEcosystemName) {
      createEcosystem();
    }
    if (industry !== research?.industry) {
      researchCompany({ companyName, email: info!.email, industries: [industry] });
    }
  }, [
    generatedEcosystemName,
    createEcosystem,
    researchCompany,
    industry,
    research,
    companyName,
    info,
  ]);

  // 2nd step functionality
  const createWorkflowsQuery = dianaAIQuery.createSeveralWorkflows();
  const { mutate: createWorkflows } = useMutation({
    mutationKey: createWorkflowsQuery.key,
    mutationFn: () =>
      createWorkflowsQuery.fn({
        ecosystem: ecosystemName,
        query: {
          industry,
          website_url: websiteUrl,
          company_research: research?.research ?? '',
          company_name: companyName,
          areas_for_improvement: keyAreas,
        },
        abort: abortWorkflowGenerate.current,
      }),
    onSuccess: () => localStorageUtil.removeItem({ key: LocalStorageKey.isGeneratingFlows }),
    onSettled: () => setIsGeneratingWorkflows(false),
  });
  const handleCreateWorkflows = useCallback(() => {
    setIsGeneratingWorkflows(true);
    createWorkflows();
  }, [setIsGeneratingWorkflows, createWorkflows]);

  // 3rd step functionality
  const handleRedirect = useCallback(async () => {
    axios.setEnvironment(ecosystemName.toLowerCase());
    setGlobalLoading(true);

    if (palette.primary.main !== color) {
      await updateEcosystem({
        colors: { primary: color, text: palette.getContrastText(color) },
      });
    }

    if (info?.companyName !== companyName) {
      await companyQuery.update().fn({ company: { ...info!.company, displayName: companyName } });
    }

    if (logoFile && !isEmptyFile(logoFile)) {
      await environmentSettingsQuery
        .updateLogo()
        .fn({ environmentName: ecosystemName, file: logoFile });
    }

    const isGeneratingFlows = useAppStore.getState().isGeneratingWorkflows;
    if (isGeneratingFlows) {
      localStorageUtil.setItem({
        key: LocalStorageKey.isGeneratingFlows,
        value: 'true',
      });
    }

    const { key, fn: getSession } = sessionInfoQuery.get();
    const sessionInfo = await getSession({ trackEnvironment: true });
    queryClient.setQueryData(key, sessionInfo);
    onSessionInfoSuccess(sessionInfo);

    // notify user via email that ecosystem is created
    environmentSettingsQuery.createComplete().fn();

    const redirectUrl = routeUrls.workflows.url({ environment: ecosystemName }, {});
    const priceId = localStorageUtil.getItem({ key: LocalStorageKey.billingPriceId });

    if (priceId && info?.isUserCompanyOwner) {
      localStorageUtil.setItem({
        key: LocalStorageKey.billingRedirectSuccess,
        value: redirectUrl,
      });

      navigate(routeUrls.checkout.url(null, null));
    } else {
      navigate(redirectUrl, { replace: true });
    }

    clearStore();
    setGlobalLoading(false);
  }, [
    onSessionInfoSuccess,
    navigate,
    setGlobalLoading,
    ecosystemName,
    companyName,
    info,
    clearStore,
    color,
    logoFile,
    palette,
    updateEcosystem,
  ]);

  const steps: IOnboardingStep[] = [
    {
      component: <BusinessForm />,
      banner: {
        title: t('steps.business.banner.title'),
        description: t('steps.business.banner.description'),
        illustration: (
          <CompanyOverviewIllustration
            userName={info?.userName}
            email={info?.email}
            companyName={companyName}
          />
        ),
      },
      onSubmit: () => handleCreateEcosystem(),
      disableSubmit: !companyName || !ecosystemName || !industry || isLoadingRequest || isError,
    },
    {
      component: <BusinessKeyAreas />,
      banner: {
        title: t('steps.keyAreas.banner.title'),
        description: t('steps.keyAreas.banner.description'),
        illustration: <WorkflowCardsIllustration />,
      },
      onSubmit: () => handleCreateWorkflows(),
      onCancel: () => {
        handleAbort(abortEcosystemCreate);
        setIsLoadingRequest(false);
      },
      isLoading: !!keyAreas.length && (isCreatingEcosystem || isResearchingCompany),
      disableSubmit: !keyAreas.length || isCreatingEcosystem,
    },
    {
      component: <CustomiseEcosystem />,
      banner: {
        title: t('steps.ecosystem.banner.title'),
        description: t('steps.ecosystem.banner.description'),
        illustration: (
          <HomePageIllustration
            ecosystemName={ecosystemName}
            userName={getUserName(info?.user)}
            primaryColor={isValidColor ? color : palette.primary.main}
            file={logoFile && !isEmptyFile(logoFile) ? logoFile : null}
          />
        ),
      },
      onSubmit: () => handleRedirect(),
      onCancel: () => handleAbort(abortWorkflowGenerate),
      disableSubmit: !isValidColor,
    },
  ];

  return steps;
};
